// src/pages/LandingPage.js
// Redesigned Landing Page with Explore/Dashboard dual mode

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './LandingPage.module.css';
import { FaPlus } from 'react-icons/fa';
import { useUser } from '../context/UserContext';
import { usePlayerProfile } from '../hooks/playerProfile/usePlayerProfile';

// New components
import { ExploreView } from '../components/Explore';
import { DashboardView } from '../components/Dashboard';
import { ModeToggle } from '../components/common';
import { useExploreData } from '../hooks/useExploreData';
import { 
  detectUserPersona, 
  getDefaultMode, 
  MODES 
} from '../utils/personaDetection';

// Existing components (preserved)
import ClubCreationWizard from '../components/ClubCreation/ClubCreationWizard';
import JoinClubModal from '../components/Club/JoinClubModal';
import ClubManagementModal from '../components/Club/ClubManagementModal';

const LandingPage = () => {
  const { user, userClubs, refreshUserClubs, userProfile } = useUser();
  const navigate = useNavigate();
  
  // Get player profile to read primary sport
  const { profile: playerProfile, loading: profileLoading } = usePlayerProfile(userProfile?._id, {
    sections: 'athleticProfile',
    skipCache: false
  });
  
  // User location
  const [userLocation, setUserLocation] = useState(null);
  
  // Mode state (explore or dashboard)
  const [activeMode, setActiveMode] = useState(null); // Will be set based on persona
  const [modeInitialized, setModeInitialized] = useState(false);
  
  // Range state
  const [rangeInMiles, setRangeInMiles] = useState(50);
  
  // Modal states (preserved from original)
  const [selectedClub, setSelectedClub] = useState(null);
  const [isAddClubModalOpen, setIsAddClubModalOpen] = useState(false);
  const [isJoinClubModalOpen, setIsJoinClubModalOpen] = useState(false);
  const [isManagementModalOpen, setIsManagementModalOpen] = useState(false);

  // Use the explore data hook
  const {
    clubs: exploreClubs,
    capacityMap,
    filters,
    updateFilter,
    updateFilters,
    clearFilter,
    resetFilters,
    activeFiltersCount,
    totalStats,
    isLoading: exploreLoading,
    refresh: refreshExplore
  } = useExploreData(userLocation, {
    rangeInMiles,
    enabled: activeMode === MODES.EXPLORE && userLocation !== null
  });

  // Set default sport from player profile
  useEffect(() => {
    if (!profileLoading && playerProfile?.athleticProfile?.primarySport) {
      const primarySport = playerProfile.athleticProfile.primarySport;
      if (filters.sport === 'All') {
        updateFilter('sport', primarySport);
      }
    }
  }, [playerProfile, profileLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize mode based on persona
  useEffect(() => {
    if (!modeInitialized && userClubs !== undefined) {
      const persona = detectUserPersona(user, userClubs);
      const defaultMode = getDefaultMode(persona);
      setActiveMode(defaultMode);
      setModeInitialized(true);
    }
  }, [user, userClubs, modeInitialized]);

  // Set user location - request geolocation on page load
  useEffect(() => {
    const cachedLocation = localStorage.getItem('userLocation');
    const cacheTimestamp = localStorage.getItem('userLocationTimestamp');
    const cacheAge = cacheTimestamp ? Date.now() - parseInt(cacheTimestamp) : Infinity;
    const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
    
    // Use cached location if it's recent (less than 30 minutes old)
    if (cachedLocation && cacheAge < CACHE_DURATION) {
      try {
        setUserLocation(JSON.parse(cachedLocation));
        return;
      } catch (e) {
        // Invalid cached data, continue to get fresh location
      }
    }
    
    // Request fresh location from browser
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          console.log('Got user location on page load:', newLocation);
          
          // Cache the location
          localStorage.setItem('userLocation', JSON.stringify(newLocation));
          localStorage.setItem('userLocationTimestamp', Date.now().toString());
          
          setUserLocation(newLocation);
        },
        (error) => {
          console.log('Geolocation denied or unavailable, using cached/default location');
          
          // Fall back to cached location if available (even if old)
          if (cachedLocation) {
            try {
              setUserLocation(JSON.parse(cachedLocation));
              return;
            } catch (e) {
              // Invalid cached data
            }
          }
          
          // Final fallback: central US location
          const defaultLocation = { lat: 39.8283, lng: -98.5795 };
          setUserLocation(defaultLocation);
        },
        {
          enableHighAccuracy: false, // Use faster, less accurate location for page load
          timeout: 5000,
          maximumAge: 10 * 60 * 1000 // Accept location up to 10 minutes old
        }
      );
    } else {
      // Browser doesn't support geolocation
      const defaultLocation = { lat: 39.8283, lng: -98.5795 };
      setUserLocation(defaultLocation);
    }
  }, []);

  // Handler for when a club marker is clicked on map
  const handleClubMarkerClick = useCallback((club) => {
    navigate(`/club/${club._id}`);
  }, [navigate]);

  // Handler for when the edit button is clicked
  const handleEditClub = useCallback((club) => {
    navigate(`/club/${club._id}/edit`);
  }, [navigate]);

  // Handler for when the view button is clicked
  const handleViewClub = useCallback((club) => {
    navigate(`/club/${club._id}`);
  }, [navigate]);

  // Handler for join club
  const handleJoinClub = useCallback((club) => {
    if (!club || !club._id) {
      alert('Club information is missing. Please try again.');
      return;
    }
    
    const cleanClub = {
      _id: club._id,
      name: club.name,
      description: club.description,
      sport: club.sport,
      type: club.type,
      clubType: club.clubType,
      clubLogo: club.clubLogo,
      contactNumber: club.contactNumber,
      street: club.street,
      city: club.city,
      state: club.state,
      zipCode: club.zipCode,
      businessType: club.businessType,
      rating: club.rating,
      membershipType: club.membershipType,
      membershipFee: club.membershipFee,
      createdBy: club.createdBy
    };
    
    setSelectedClub(cleanClub);
    setIsJoinClubModalOpen(true);
  }, []);

  // Handler for join success
  const handleJoinSuccess = useCallback(() => {
    setIsJoinClubModalOpen(false);
    setSelectedClub(null);
    refreshUserClubs();
    refreshExplore();
  }, [refreshUserClubs, refreshExplore]);

  // Handle range change from map
  const handleRangeChange = useCallback((newRange) => {
    setRangeInMiles(newRange);
  }, []);

  // Handle location change from map (zipcode search or drag)
  const handleLocationChange = useCallback((newLocation) => {
    setUserLocation(newLocation);
  }, []);

  // Switch to explore mode
  const handleExploreClick = useCallback(() => {
    setActiveMode(MODES.EXPLORE);
  }, []);

  // Handler to refresh data after adding a club
  const handleClubCreated = useCallback((newClub) => {
    refreshUserClubs();
    refreshExplore();
    if (newClub?._id) {
      navigate(`/club/${newClub._id}`);
    }
  }, [refreshUserClubs, refreshExplore, navigate]);

  // Don't render until mode is initialized
  if (!modeInitialized) {
    return (
      <div className={styles.landingContainer}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.landingContainer}>
      {/* Twitter/X-style Tab Bar */}
      <div className={styles.stickyTabBar}>
        <ModeToggle activeMode={activeMode} onModeChange={setActiveMode} />
      </div>

      {/* Content based on active mode */}
      {activeMode === MODES.EXPLORE ? (
        <ExploreView
          userLocation={userLocation}
          clubs={exploreClubs}
          capacityMap={capacityMap}
          filters={filters}
          updateFilter={updateFilter}
          updateFilters={updateFilters}
          clearFilter={clearFilter}
          resetFilters={resetFilters}
          activeFiltersCount={activeFiltersCount}
          totalStats={totalStats}
          isLoading={exploreLoading}
          rangeInMiles={rangeInMiles}
          onRangeChange={handleRangeChange}
          onRefresh={refreshExplore}
          userClubs={userClubs}
          onClubMarkerClick={handleClubMarkerClick}
          onLocationChange={handleLocationChange}
          activeMode={activeMode}
          onModeChange={setActiveMode}
        />
      ) : (
        <DashboardView
          user={user}
          userProfile={userProfile}
          userClubs={userClubs}
          refreshUserClubs={refreshUserClubs}
          onViewClub={handleViewClub}
          onEditClub={handleEditClub}
          onJoinClub={handleJoinClub}
          onExploreClick={handleExploreClick}
          activeMode={activeMode}
          onModeChange={setActiveMode}
        />
      )}

      {/* Floating Create Club Button */}
      <button
        className={styles.floatingAddButton}
        onClick={() => setIsAddClubModalOpen(true)}
        aria-label="Create New Club"
      >
        <FaPlus className={styles.floatingAddIcon} />
        <span className={styles.floatingAddText}>Create Club</span>
      </button>

      {/* Modals - All preserved from original */}
      {isAddClubModalOpen && (
        <ClubCreationWizard
          isOpen={isAddClubModalOpen}
          onClose={() => setIsAddClubModalOpen(false)}
          onComplete={handleClubCreated}
          userLocation={userLocation}
        />
      )}

      {isJoinClubModalOpen && selectedClub && (
        <JoinClubModal
          club={selectedClub}
          onClose={() => {
            setIsJoinClubModalOpen(false);
            setSelectedClub(null);
          }}
          onJoinSuccess={handleJoinSuccess}
          user={user}
        />
      )}

      {isManagementModalOpen && selectedClub && (
        <ClubManagementModal
          club={selectedClub}
          onClose={() => {
            setIsManagementModalOpen(false);
            setSelectedClub(null);
          }}
          user={user}
        />
      )}
    </div>
  );
};

export default LandingPage;
